import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BackendApiService } from './backend-api.service';

/** The two Queue-it waiting-room events the apps can send a visitor through. */
export const QUEUEIT_EVENT_TYPE = {
	contract: 'contract',
	booking: 'booking'
} as const;

export type QueueItEventType = (typeof QUEUEIT_EVENT_TYPE)[keyof typeof QUEUEIT_EVENT_TYPE];

/** Payload the backend's `queueitValidateV3` / `queueitCancelV3` / `serviceCreateV3` /
 *  `createBookingConfirmV3` expect. */
export interface QueueItRequest {
	eventType: QueueItEventType;
	targetUrl: string;
	queueitToken: string;
}

/**
 * Shared Queue-it client used by public (booking page) and CEM (contract create).
 *
 * Both apps previously inlined the same token + validate/cancel/redirect sequence.
 * `Authorization` and `X-Spaq-Scope` are stamped by each app's `ScopeInterceptor`,
 * not here. Callers must pass which event they are gating — there is no default.
 */
@Injectable({ providedIn: 'root' })
export class QueueItService {
	constructor(private backendApi: BackendApiService) {}

	/**
	 * Reads the current page URL and the `queueittoken` query param Queue-it
	 * appends when redirecting a visitor back from the waiting room. Without
	 * forwarding that token, the backend can never recognize a completed queue
	 * pass and re-gates every return trip — an infinite redirect loop.
	 */
	static getRequest(eventType: QueueItEventType): QueueItRequest {
		return {
			eventType,
			targetUrl: window.location.href,
			queueitToken: new URLSearchParams(window.location.search).get('queueittoken') ?? ''
		};
	}

	/**
	 * Asks the backend whether this visitor still needs to wait. If so, replaces
	 * the current page with the Queue-it waiting-room URL and resolves `true`.
	 * Resolves `false` when the visitor may proceed. Fail-open: a backend error
	 * is treated as not required.
	 */
	async check(eventType: QueueItEventType): Promise<boolean> {
		try {
			const res = await firstValueFrom(this.backendApi.queueitValidate(QueueItService.getRequest(eventType)));
			if (res?.queueItRequired && res.redirectUrl) {
				window.location.href = res.redirectUrl;
			}
			return res?.queueItRequired ?? false;
		} catch {
			// fail-open
		}
		return false;
	}

	static getExpirationDate(): Date | null {
		const token = new URLSearchParams(window.location.search).get('queueittoken');

		if (!token) {
			return null;
		}

		const tsPart = token.split('~').find(part => part.startsWith('ts_'));

		if (!tsPart) {
			return null;
		}

		const ts = Number(tsPart.substring(3));

		if (!Number.isFinite(ts) || ts <= 0) {
			return null;
		}

		return new Date(ts * 1000);
	}
}
